import React, { useState, useRef } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../Redux/store";
import InputTextField from "../../Views/inputtextfield/InputTextField";
import Button from "../../Views/buttons/Button";
import beApi from "../../API/beApi";
import { toast } from "react-toastify";
import { CATCH_MESSAGE, validateEmail } from "../../UtilityFunctions";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

interface IVoucherSlider {
  onClose: () => void;
  bookingId: any;
  bookingSrc: any;
  voucher: any;
}

const VoucherSlider: React.FC<IVoucherSlider> = ({
  onClose,
  bookingId,
  voucher,
  bookingSrc,
}) => {
  // Redux
  const { auth_token } = useSelector((state: RootState) => state.auth);

  const voucherTableRef = useRef<any>(null);

  // State
  const [emailId, setEmailId] = useState<any>("");
  const [emailHelperText, setEmailHelperText] = useState<any>("");
  const [loader, setLoader] = useState<boolean>(false);
  const [downloadLoader, setDownloadLoader] = useState<boolean>(false);

  // send email handler
  const sendEmail = async () => {
    setLoader(true);
    if (emailHelperText || !emailId) {
      toast.error("Enter a valid email Id");
      setLoader(false);
      return;
    }
    try {
      const response = await beApi.post(`/voucher-send-mail`, {
        email: emailId,
        booking_id: bookingId,
        source: bookingSrc,
      });
      if (response.data.status === 1) {
        toast.success(response.data.message);
        setEmailId("");
        setEmailHelperText("");
        setLoader(false);
      } else {
        setLoader(false);
      }
    } catch {
      setLoader(false);
      toast.error("Something went wrong!");
    }
  };

  // Download Voucher as PDF
  const downloadVoucherTable = async () => {
    setDownloadLoader(true);
    const input = voucherTableRef.current;
    if (!input) {
      toast.error("Voucher content not found");
      setDownloadLoader(false);
      return;
    }

    try {
      await new Promise((resolve) => setTimeout(resolve, 300));
      const canvas = await html2canvas(input, {
        useCORS: true,
        allowTaint: true,
        scale: 2,
        logging: false,
        backgroundColor: "#ffffff",
        removeContainer: true,
        onclone: (clonedDoc) => {
          // Fix all images to preserve aspect ratio
          const images = clonedDoc.querySelectorAll("img");
          images.forEach((img: any) => {
            if (img.naturalWidth && img.naturalHeight) {
              const aspectRatio = img.naturalWidth / img.naturalHeight;
              // Set height to 60px and calculate width automatically
              img.style.height = "60px";
              img.style.width = 60 * aspectRatio + "px";
            }
            img.style.objectFit = "contain";
          });
          // Remove all background images to prevent zero-dimension errors
          const allElements = clonedDoc.querySelectorAll("*");
          allElements.forEach((el: any) => {
            if (el.style) {
              el.style.backgroundImage = "none";
            }
          });
        },
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.95);
      const imgWidth = 210; // A4 width in mm (full width, no margins)
      const pageHeight = 297; // A4 height in mm (full height, no margins)
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const doc = new jsPDF("p", "mm", "a4");
      let heightLeft = imgHeight;
      let position = 0;

      // Add first page
      doc.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages if needed
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        doc.addPage();
        doc.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      doc.save(`voucher_${bookingId}.pdf`);
      toast.success("Voucher downloaded successfully");
    } catch (err: any) {
      console.error("Error generating PDF:", err);
      toast.error("Failed to download voucher. Please try again.");
    } finally {
      setDownloadLoader(false);
    }
  };

  return (
    <>
      <div className="side__slider__header__label__wrapper">
        <div className="side__slider__close">
          <i className="bi bi-x" onClick={onClose}></i>
        </div>
        <div className="standard-page--label">Voucher (ID: {bookingId})</div>
      </div>
      <div className="height-64px"></div>
      <div className="fetch__booking__wrapper">
        <div
          ref={voucherTableRef}
          dangerouslySetInnerHTML={{ __html: voucher.data }}
        ></div>
        <div style={{ height: "102px" }} />
      </div>
      <div className="side__slider__panel__btn">
        <div
          className="__btn__wrapper"
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "16px",
            width: "100%",
          }}
        >
          <Button
            loader={downloadLoader}
            handleClick={() => downloadVoucherTable()}
            className="mt-2"
          >
            Download
          </Button>
          <div style={{ flex: 1 }}>
            <InputTextField
              label="Email Address"
              value={emailId}
              handleChange={(e) => {
                setEmailId(e.target.value);
                if (!validateEmail(e.target.value)) {
                  setEmailHelperText("***Not a valid Email Id");
                } else {
                  setEmailHelperText("");
                }
              }}
              helperText={emailHelperText}
            />
          </div>
          <Button
            className="mt-2"
            handleClick={() => sendEmail()}
            loader={loader}
          >
            Send
          </Button>
        </div>
      </div>
    </>
  );
};

export default VoucherSlider;
